<template>
  <div :class="['d-flex flex-column min-w-0', { 'min-h-0': rightBarTab }]">
    <v-row
      class="ma-0"
      no-gutters
    >
      <v-col
        :class="[
          'ma-0',
          $vuetify.display.mdAndUp ? 'pa-0' : 'px-0',
          rightBarTab ? 'order-2 order-md-2 mt-4' : 'order-md-1',
        ]"
        :md="rightBarTab ? 12 : 7"
        cols="12"
      >
        <StandardCard
          v-if="workflowInstance"
          card-text-class="pa-0"
          class="mb-3"
        >
          <v-stepper-vertical
            v-model="expandedPhaseIndices"
            :mandatory="false"
            class="license-workflow-phase-stepper"
            flat
            hide-actions
            multiple
            non-linear
          >
            <v-stepper-vertical-item
              v-for="(phase, phaseIndex) in workflowInstance.phases ?? []"
              :key="phase.id"
              :color="phase.completed ? 'success' : 'light-grey'"
              :complete="phase.completed"
              :edit-icon="null"
              :icon="phaseStepperIcon(phase)"
              :subtitle="phase.description || undefined"
              :title="phaseStepperTitle(phase, phaseIndex)"
              :value="phaseIndex + 1"
              editable
            >
              <template #title="{ title }">
                <div class="d-flex align-center ga-2 min-w-0 w-100">
                  <span class="fw-800 text-high-emphasis text-wrap min-w-0 flex-grow-1">
                    {{ title }}
                  </span>

                  <StandardChip
                    :color="phaseBadgeColor(phase)"
                    :text="phaseBadgeText(phase)"
                    :text-translate="false"
                    class="flex-shrink-0"
                    role="status"
                  />
                </div>
              </template>

              <template #subtitle="{ subtitle }">
                <span
                  v-if="subtitle"
                  class="text-body-1 text-medium-emphasis text-wrap d-block w-80pc"
                >
                  {{ subtitle }}
                </span>
              </template>

              <LicenseWorkflowPhasePanel
                @assign-task="emit('assign-task', $event)"
                @review-task="emit('review-task', $event)"
                @update:task-expanded-indices="
                  (indices) => setTaskExpandedIndicesForPhase(phase.id, indices)
                "
                :locked="isPhaseLocked(phase)"
                :phase="phase"
                :task-expanded-indices="expandedTaskIndicesByPhase[phase.id] ?? []"
              />
            </v-stepper-vertical-item>
          </v-stepper-vertical>
        </StandardCard>
      </v-col>

      <v-col
        :class="[
          'ma-0',
          rightBarTab ? 'order-1 order-md-1' : 'order-md-2',
          $vuetify.display.mdAndUp ? (rightBarTab ? 'pa-0' : 'ps-5 pa-0') : 'px-0',
        ]"
        :md="rightBarTab ? 12 : 5"
        cols="12"
      >
        <div
          v-if="showPublicRecordCard"
          class="mb-4"
        >
          <PublicRecordCard
            @save="emit('reload')"
            :license-id="licenseId"
            :public-record-id="publicRecordArtifactId"
            :schema-id="workflowInstance?.public_record_schema_id ?? null"
            :workflow-instance-id="workflowInstance.id"
          />
        </div>

        <FormArtifactCard
          v-if="showFormArtifactCard"
          :form-artifact="formArtifact"
          class="mb-3"
        />

        <LicenseAssignmentCard
          @save="emit('workflow-instance-updated', $event)"
          :workflow-instance="workflowInstance"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import { useStore } from 'vuex';

import FormArtifactCard from '@/shared/components/forms/FormArtifactCard.vue';
import LicenseAssignmentCard from '@/shared/components/licenses/LicenseAssignmentCard.vue';
import LicenseWorkflowPhasePanel from '@/shared/components/licenses/LicenseWorkflowPhasePanel.vue';
import PublicRecordCard from '@/shared/components/public-records/PublicRecordCard.vue';
import { statusColorNameForKey } from '@/shared/composables/useStatusChipColors';
import useRole from '@/shared/composables/useRole';

const { isSpecialist } = useRole();
const store = useStore();

const $bct = inject('translator');

const props = defineProps({
  licenseId: {
    type: String,
    default: null,
  },
  rightBarTab: {
    type: String,
    default: null,
  },
  workflowInstance: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['assign-task', 'reload', 'review-task', 'workflow-instance-updated']);

const expandedPhaseIndices = ref([]);
const expandedTaskIndicesByPhase = ref({});

const publicRecordArtifactId = computed(() => {
  const artifact = props.workflowInstance?.workflow_artifacts?.find(
    (artifact) => artifact.artifact_type === 'PublicRecord',
  );

  return artifact?.artifact_id ?? null;
});

const formArtifact = computed(
  () =>
    props.workflowInstance?.workflow_artifacts?.find(
      (artifact) => artifact.artifact_type === 'Form',
    ) ?? null,
);

const showFormArtifactCard = computed(() => {
  if (!isSpecialist) return false;

  return Boolean(formArtifact.value);
});

const publicRecordSchemas = computed(() =>
  Object.values(store.state.schemas || {}).filter((schema) => schema.data_type === 'PublicRecord'),
);

function isPublicRecordSchemaInStore(schemaId) {
  return store.state.schemas?.[schemaId]?.data_type === 'PublicRecord';
}

const showPublicRecordCard = computed(() => {
  if (!isSpecialist) return false;
  if (!props.licenseId) return false;
  if (publicRecordArtifactId.value) return true;
  if (!props.workflowInstance?.allows_public_record) return false;

  const pinnedSchemaId = props.workflowInstance?.public_record_schema_id ?? null;
  if (pinnedSchemaId) return isPublicRecordSchemaInStore(pinnedSchemaId);

  return publicRecordSchemas.value.length > 0;
});

function isPhaseLocked(phase) {
  if (phase.completed) return false;

  const phases = props.workflowInstance?.phases ?? [];
  const phaseIndex = phases.findIndex((item) => item.id === phase.id);

  if (phaseIndex > 0) {
    const priorPhasesIncomplete = phases
      .slice(0, phaseIndex)
      .some((priorPhase) => !priorPhase.completed);

    if (priorPhasesIncomplete) return true;
  }

  const tasks = phase.tasks ?? [];

  if (tasks.length === 0) return false;

  return tasks.every((task) => task.status === 'pending');
}

function phaseBadgeColor(phase) {
  const kind = phaseBadgeKind(phase);
  if (['completed', 'locked'].includes(kind)) return statusColorNameForKey(kind);

  return statusColorNameForKey('active');
}

function phaseBadgeKind(phase) {
  if (phase.completed) return 'completed';
  if (isPhaseLocked(phase)) return 'locked';

  return 'active';
}

function phaseBadgeText(phase) {
  const kind = phaseBadgeKind(phase);

  if (kind === 'completed') return $bct('Completed');
  if (kind === 'locked') return $bct('Locked');

  const reviewed = phaseReviewedCount(phase);
  const total = phaseTasksTotal(phase);

  return `${reviewed} of ${total} reviewed`;
}

function phaseReviewedCount(phase) {
  return (phase.tasks ?? []).filter((task) => task.status === 'completed').length;
}

function phaseStepperIcon(phase) {
  if (phase.completed) return 'check_circle';
  if (isPhaseLocked(phase)) return 'lock';

  return 'radio_button_unchecked';
}

function phaseStepperTitle(phase, phaseIndex) {
  const phases = props.workflowInstance?.phases ?? [];
  const total = phases.length;
  const phaseNumber = phaseIndex + 1;

  return `Phase ${phaseNumber} of ${total}: ${phase.name}`;
}

function phaseTasksTotal(phase) {
  return phase.tasks?.length ?? 0;
}

function setTaskExpandedIndicesForPhase(phaseId, indices) {
  expandedTaskIndicesByPhase.value = {
    ...expandedTaskIndicesByPhase.value,
    [phaseId]: indices,
  };
}

watch(
  () => props.workflowInstance?.phases,
  (phases) => {
    if (!phases?.length) {
      expandedPhaseIndices.value = [];
      expandedTaskIndicesByPhase.value = {};
      return;
    }

    expandedPhaseIndices.value = phases.flatMap((phase, phaseIndex) =>
      phase.completed || isPhaseLocked(phase) ? [] : [phaseIndex + 1],
    );

    const nextTaskExpansion = {};
    for (const phase of phases) {
      const tasks = phase.tasks ?? [];
      nextTaskExpansion[phase.id] = isPhaseLocked(phase)
        ? []
        : tasks.map((_task, taskIndex) => taskIndex + 1);
    }
    expandedTaskIndicesByPhase.value = nextTaskExpansion;
  },
  { immediate: true },
);
</script>

<style scoped>
.license-workflow-phase-stepper :deep(.v-expansion-panel-title > .v-avatar + div) {
  flex: 1 1 auto;
  min-width: 0;
}

.license-workflow-phase-stepper :deep(.v-expansion-panel-title__icon) {
  display: none;
}

.license-workflow-phase-stepper :deep(.v-stepper-vertical-item__avatar.v-avatar) {
  align-items: center;
  display: flex;
  justify-content: center;
}
</style>
